import { useCallback, useRef, useState } from 'react';

/**
 * The measured width of one element, kept live.
 *
 * The habit chart decides how many days it shows from how much room it has,
 * and that is a layout fact the render has to know — a media query can only
 * say "desktop", not "1,144 pixels of card". A callback ref rather than an
 * effect, so the first measurement happens as soon as the node exists.
 */
export function useElementWidth(): [(node: HTMLElement | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;

    // Absent in jsdom, and in any environment without layout. Zero then means
    // the minimum window, which is the right answer for a screen we can't see.
    if (typeof ResizeObserver !== 'function') {
      setWidth(node.clientWidth);
      return;
    }

    const next = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    next.observe(node);
    observer.current = next;
  }, []);

  return [ref, width];
}
